import { UserWithCustomerResource, ViewUserResource, usePatchProfileDetailsMutation } from '@/api/serverApi'
import { Button, InputLabel, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

type PropTypes = {
  user: UserWithCustomerResource | ViewUserResource
}

const editProfileFormSchema = v.object({
  name: v.string(),
  email: v.string([v.email()]),
})

type EditProfileFormInputs = v.Input<typeof editProfileFormSchema>

function EditProfileForm ({ user }: PropTypes) {
  const textSpec = {
    title: 'General',
    nameField: {
      label: 'Name',
      placeholder: 'Name',
    },
    emailField: {
      label: 'Email',
      placeholder: 'Email',
    },
    submitButtonLabel: 'Save Changes',
    successToastMessage: {
      title: 'Details changed successfully',
      body: 'Your details have now been changed, if you changed your email address, please check your emails.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { register, handleSubmit, formState: { errors }, setError } = useForm<EditProfileFormInputs>({
    defaultValues: {
      name: user.name ?? '',
      email: user.email ?? '',
    },
    resolver: valibotResolver(editProfileFormSchema),
  })

  const [update, { isLoading: isSubmitting }] = usePatchProfileDetailsMutation()

  const onSubmit: SubmitHandler<EditProfileFormInputs> = (data) => {
    update({ body: data })
      .unwrap()
      .then((_) => {
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      <h2 className="mb-[25px] text-lg leading-[21.6px] font-semibold">{textSpec.title}</h2>

      <div className="mb-[30px] w-full">
        <InputLabel name="name">{textSpec.nameField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.nameField.placeholder}
          error={errors?.name?.message}
          {...register('name')}
        />
      </div>

      <div className="mb-[30px] w-full">
        <InputLabel name="email">{textSpec.emailField.label}</InputLabel>
        <TextInput
          placeholder={textSpec.emailField.placeholder}
          error={errors?.email?.message}
          {...register('email')}
        />
      </div>

      <div className="w-full flex justify-end">
        <div className="mt-2">
          <Button
            variant="dark"
            isLoading={isSubmitting}
            type="submit"
            isBlock
          >
            {textSpec.submitButtonLabel}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default EditProfileForm
